import { computed, ref } from 'vue';
import NucleoWorkOrderRequestsAPI from 'dashboard/api/nucleoWorkOrderRequests';
import {
  initialNucleoRequestForm,
  setDistrictCount as resizeDistricts,
  setServiceCount as resizeServices,
  updateCondition as changeCondition,
  updateDistrict as changeDistrict,
  updateService as changeService,
} from './nucleoWorkOrderRequestForm';
import {
  findServiceEntry,
  validateNucleoWorkOrderRequest,
} from './nucleoWorkOrderRequestValidation';
const requestKey = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function useNucleoWorkOrderRequest({ transactionId, notify, t }) {
  const catalog = ref(null);
  const form = ref(initialNucleoRequestForm());
  const errors = ref({});
  const loading = ref(false);
  const saving = ref(false);
  const confirmOpen = ref(false);
  const idempotencyKey = ref(requestKey());

  const configured = computed(() => Boolean(catalog.value?.configured));
  const serviceTypes = computed(() => catalog.value?.service_types || []);
  const sellers = computed(() => catalog.value?.sellers || []);
  const states = computed(() => catalog.value?.states || []);

  const load = async () => {
    loading.value = true;
    try {
      const response = await NucleoWorkOrderRequestsAPI.catalog(
        transactionId.value
      );
      catalog.value = response.data.data;
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('NUCLEO_WORK_ORDERS.REQUEST.ERRORS.LOAD')
      );
    } finally {
      loading.value = false;
    }
  };

  const setServiceCount = value => {
    form.value = resizeServices(form.value, value);
    errors.value = {};
  };

  const updateService = (index, field, value) => {
    form.value = changeService(form.value, index, field, value);
    errors.value = {};
  };

  const setDistrictCount = value => {
    form.value = resizeDistricts(form.value, value);
    errors.value = {};
  };

  const updateCondition = (field, value) => {
    form.value = changeCondition(form.value, field, value);
    errors.value = {};
  };

  const updateDistrict = (index, field, value) => {
    form.value = changeDistrict(form.value, index, field, value);
    errors.value = {};
  };

  const serviceEntry = service => findServiceEntry(serviceTypes.value, service);

  const validate = () => {
    const next = validateNucleoWorkOrderRequest({
      configured: configured.value,
      form: form.value,
      serviceTypes: serviceTypes.value,
      t,
    });
    errors.value = next;
    return Object.keys(next).length === 0;
  };

  const review = () => {
    if (!validate()) return false;
    confirmOpen.value = true;
    return true;
  };

  const submit = async () => {
    saving.value = true;
    try {
      const response = await NucleoWorkOrderRequestsAPI.create(
        transactionId.value,
        form.value,
        idempotencyKey.value
      );
      confirmOpen.value = false;
      idempotencyKey.value = requestKey();
      notify(t('NUCLEO_WORK_ORDERS.REQUEST.ALERTS.SENT'));
      return response.data.data;
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          t('NUCLEO_WORK_ORDERS.REQUEST.ERRORS.SEND')
      );
      return null;
    } finally {
      saving.value = false;
    }
  };

  return {
    catalog,
    configured,
    confirmOpen,
    errors,
    form,
    load,
    loading,
    review,
    saving,
    sellers,
    serviceEntry,
    serviceTypes,
    setDistrictCount,
    setServiceCount,
    states,
    submit,
    updateCondition,
    updateDistrict,
    updateService,
  };
}
